import React, { useState } from 'react'
import { Translate } from 'react-localize-redux'
import {
  Box,
  Typography,
  IconButton,
  CardActionArea,
  Divider,
  Tooltip,
} from '@material-ui/core'
import PropTypes from 'prop-types'
import ShareIcon from '@material-ui/icons/Share'
import FavoriteIcon from '@material-ui/icons/Favorite'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import { SubLink } from 'containers'
import { AlertDialog } from 'components'
import { connect } from 'react-redux'
import useFeatureAccessGuard from 'hooks/useFeatureAccessGuard'
import useTeamFeature from 'hooks/useTeamFeature'
import PremiumContentDialog from '../../../components/PremiumContentDialog'
import LockIconComponent from 'components/UpgradeMembership/LockIconComponent'
import fallbackImageUrl from '../../../assets/images/login-coach.jpg'
import {
  MuiCard,
  MuiBox,
  CardMedia,
  CardContent,
  CardActions,
} from './StyledComponents'
import ArticleViewDialog from './ArticleViewDialog'
import { ARTICLE_EDIT_ROUTE } from 'constants/routes'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const ArticleCard = ({
  article,
  onFavoriteToggle,
  language,
  timezone,
  teamUuid,
  showArticleActions,
  onDeleteArticle,
}) => {
  const featureGuard = useFeatureAccessGuard()
  const { enabled: hasKnowledgeEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.knowledge_enabled
  )

  const [articleViewDialogOpen, setArticleViewDialogOpen] = useState(false)
  const [alertOpen, setAlertOpen] = useState(false)
  const [premiumDialogOpen, setPremiumDialogOpen] = useState(false)

  const toggleArticleViewDialogOpen = () => {
    if (featureGuard(hasKnowledgeEnabled)) return

    setArticleViewDialogOpen(!articleViewDialogOpen)
  }

  const handleToggleAlertBox = () => {
    setAlertOpen(!alertOpen)
  }

  const handleShareClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const shareUrl = `${process.env.REACT_APP_API_URL}/articles/${article.uuid}/share?language=${language}&time_zone=${timezone}&team=${teamUuid}`
    window.open(shareUrl, '_blank')
  }

  const handleCardClick = () => {
    if (
      !hasKnowledgeEnabled ||
      (article.locked && article.locked_ff === 'cb_premium_content')
    ) {
      featureGuard()
    } else if (article.locked && article.locked_ff !== 'cb_premium_content') {
      setPremiumDialogOpen(true)
    } else {
      toggleArticleViewDialogOpen()
    }
  }

  const handleClosePremiumDialog = () => {
    setPremiumDialogOpen(false)
  }

  const canUpdate = article.can.includes('update')
  const canDelete = article.can.includes('delete')

  return (
    <>
      {articleViewDialogOpen && (
        <ArticleViewDialog
          article={article}
          handleOpenClose={toggleArticleViewDialogOpen}
          open={articleViewDialogOpen}
        />
      )}
      {premiumDialogOpen && (
        <PremiumContentDialog
          open={premiumDialogOpen}
          onClose={handleClosePremiumDialog}
          lockedFeatureFlag={article.locked_ff}
        />
      )}
      <MuiCard>
        <CardActionArea onClick={handleCardClick}>
          <CardMedia
            image={
              article.relationships.thumbnail?.full_url || fallbackImageUrl
            }
          />
          <CardContent>
            <Typography gutterBottom variant="h4" component="h4" noWrap>
              {article.title}
              {article.locked ? (
                <LockIconComponent
                  context="secondaryButton"
                  style={{ float: 'right' }}
                />
              ) : null}
            </Typography>
            <MuiBox>
              <Box>
                <Typography>
                  <Translate id="time.reading-time" />:{' '}
                </Typography>
                <Typography>
                  {typeof article.content.reading_time === 'number' &&
                  article.content.reading_time !== null
                    ? `${article.content.reading_time} min`
                    : '-'}
                </Typography>
              </Box>
            </MuiBox>
          </CardContent>
        </CardActionArea>
        <Divider />
        <CardActions>
          {showArticleActions && !article.locked && (
            <>
              {canUpdate && (
                <Tooltip title={<Translate id="button.edit" />}>
                  <IconButton
                    size="small"
                    component={SubLink}
                    to={ARTICLE_EDIT_ROUTE.populateURLParams({
                      articleId: article.uuid,
                    })}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title={<Translate id="button.delete" />}>
                  <IconButton onClick={handleToggleAlertBox} size="small">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {alertOpen && (
                <AlertDialog
                  open={alertOpen}
                  onClose={handleToggleAlertBox}
                  onSuccess={onDeleteArticle}
                  onCancel={handleToggleAlertBox}
                  title={<Translate id="button.delete" />}
                  subTitle={
                    <Translate id="events.are-you-sure-you-want-to-delete" />
                  }
                  submitButtonLabel={<Translate id="button.ok" />}
                  cancelButtonLabel={<Translate id="button.close" />}
                />
              )}
              <Tooltip title={<Translate id="button.share" />}>
                <IconButton onClick={handleShareClick} size="small">
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <Tooltip title={<Translate id="drills.favorite" />}>
            <IconButton onClick={onFavoriteToggle} size="small">
              <FavoriteIcon
                color={article.favorite ? 'primary' : 'inherit'}
                fontSize="small"
              />
            </IconButton>
          </Tooltip>
        </CardActions>
      </MuiCard>
    </>
  )
}

ArticleCard.defaultProps = {
  article: {
    min_players: 0,
    relationships: {
      images: [],
    },
  },
  showArticleActions: true,
  onDeleteArticle: () => {},
}

ArticleCard.propTypes = {
  article: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    favorite: PropTypes.bool.isRequired,
    content: PropTypes.shape({
      reading_time: PropTypes.number,
    }),
    relationships: PropTypes.shape({
      thumbnail: PropTypes.shape({
        full_url: PropTypes.string,
      }),
      images: PropTypes.arrayOf(
        PropTypes.shape({
          full_url: PropTypes.string,
        })
      ),
    }),
    can: PropTypes.arrayOf(PropTypes.string),
    min_players: PropTypes.number,
    max_players: PropTypes.number,
    locked: PropTypes.bool.isRequired,
    locked_ff: PropTypes.string,
  }),
  onFavoriteToggle: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
  teamUuid: PropTypes.string.isRequired,
  showArticleActions: PropTypes.bool,
  onDeleteArticle: PropTypes.func,
}

export default connect(({ team, auth }) => ({
  teamUuid: team.uuid,
  timezone: auth.user.tz || auth.user.time_zone,
  language: auth.user.language,
}))(ArticleCard)
